"use client";

import { useEffect, useCallback, useRef, useState } from "react";
import DynamicGlobe from "@/lib/DynamicGlobe";
import { useTraceStore, type Hop } from "@/store/useTraceStore";
import { useCableLoader } from "@/hooks/useCableLoader";
import { useAnimationSequencer } from "@/hooks/useAnimationSequencer";
import HeroInput from "@/components/HeroInput";
import CompactHeader from "@/components/CompactHeader";
import Sidebar from "@/components/Sidebar";
import TimeScrubber from "@/components/TimeScrubber";
import LayerToggles from "@/components/LayerToggles";

/* ─────────────────────── Hop type sanitization ─────────────────────── */

/**
 * Known valid values for Hop["type"].
 * Kept as a runtime Set (not just a TypeScript type) so we can validate
 * values that arrive over the network, where the type system can't help.
 */
const VALID_HOP_TYPES = new Set<Hop["type"]>([
  "origin",
  "transit",
  "ixp",
  "cable",
  "destination",
]);

/**
 * Normalise the `type` field on every hop returned from the API or
 * showcase cache.  An unexpected value (e.g. from a future server change
 * or a corrupt cache entry) would otherwise silently reach the globe
 * render functions as an unknown colour/radius, masking data quality issues.
 * Clamping to "transit" is safe — it renders as a neutral cyan marker.
 */
function sanitizeHops(raw: Hop[]): Hop[] {
  return raw.map((hop) => {
    if (VALID_HOP_TYPES.has(hop.type)) return hop;
    console.warn(`sanitizeHops: unexpected hop type "${hop.type}" at hop ${hop.num} — clamping to "transit"`);
    return { ...hop, type: "transit" as const };
  });
}

/* ═══════════════════════════ MAIN PAGE ══════════════════════════ */

export default function HomePage() {
  const phase = useTraceStore((s) => s.phase);
  const setPhase = useTraceStore((s) => s.setPhase);
  const targetUrl = useTraceStore((s) => s.targetUrl);
  const setTargetUrl = useTraceStore((s) => s.setTargetUrl);
  const setHops = useTraceStore((s) => s.setHops);
  const setProbe = useTraceStore((s) => s.setProbe);
  const setActiveHopIndex = useTraceStore((s) => s.setActiveHopIndex);
  const reset = useTraceStore((s) => s.reset);

  /* ──── Cable data + animation sequencer ──── */
  const { cableNameMapRef, cablesLoadedRef } = useCableLoader();
  useAnimationSequencer(cableNameMapRef, cablesLoadedRef);

  /* ──── Abort controller for in-flight API requests ──── */
  const abortRef = useRef<AbortController | null>(null);

  /* ──── Trace error state ──── */
  const [traceError, setTraceError] = useState<string | null>(null);

  /* ──── Start trace (live API with geo-aware probe) ──── */
  const startTrace = useCallback(
    async (domain: string) => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      setTargetUrl(domain);
      setTraceError(null);
      setPhase("loading");

      try {
        const res = await fetch("/api/trace", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ domain }),
          signal: controller.signal,
        });

        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error((data as { message?: string }).message || `Trace failed (${res.status})`);
        }

        const { hops: traceHops, probe: traceProbe } = await res.json();
        setHops(sanitizeHops(traceHops));
        if (traceProbe) setProbe(traceProbe);
        setPhase("animating");
        setActiveHopIndex(0);
      } catch (err: unknown) {
        if (err instanceof Error && err.name === "AbortError") return;
        console.error("Trace failed:", err);

        // Map server error messages to friendly user-facing strings
        const raw = err instanceof Error ? err.message : "";
        let userMessage: string;
        if (raw.includes("Rate limit exceeded") || raw.includes("rate limit")) {
          userMessage = "Rate limit reached (10 traces/hour). Please try again later.";
        } else if (raw.includes("timed out") || raw.includes("timeout")) {
          userMessage = "Traceroute timed out — the destination may be unreachable. Try another domain.";
        } else if (raw.includes("No traceroute probes")) {
          userMessage = "No probes available right now. Please try again in a moment.";
        } else if (raw.includes("Not enough geolocatable")) {
          userMessage = "Couldn't map enough hops. The route may use heavily filtered infrastructure.";
        } else if (raw.includes("Invalid domain") || raw.includes("valid public domain")) {
          userMessage = raw; // server message is already user-friendly
        } else if (raw) {
          userMessage = raw;
        } else {
          userMessage = "Failed to trace route. Please try again.";
        }

        setTraceError(userMessage);
        setPhase("idle");
      }
    },
    [setTargetUrl, setPhase, setHops, setProbe, setActiveHopIndex]
  );

  /* ──── Cleanup abort controller on unmount ──── */
  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  /* ──── Handle new trace / reset ──── */
  const handleReset = useCallback(() => {
    abortRef.current?.abort();
    setTraceError(null);
    reset();
  }, [reset]);

  /* ──── ARIA live announcements for screen readers ──── */
  const hops = useTraceStore((s) => s.hops);
  const activeHopIndex = useTraceStore((s) => s.activeHopIndex);
  const [announcement, setAnnouncement] = useState("");

  useEffect(() => {
    if (phase === "loading") {
      setAnnouncement(`Tracing route to ${targetUrl}. Please wait.`);
    } else if (phase === "animating" && activeHopIndex >= 0 && activeHopIndex < hops.length) {
      const hop = hops[activeHopIndex];
      setAnnouncement(`Hop ${hop.num}: ${hop.city}, ${hop.country}. ${hop.latency} milliseconds.`);
    } else if (phase === "complete" && hops.length > 0) {
      const dest = hops[hops.length - 1];
      setAnnouncement(`Trace complete. ${hops.length} hops to ${dest.city}, ${dest.country}. Total latency: ${dest.cumLatency} milliseconds.`);
    }
  }, [phase, activeHopIndex, hops, targetUrl]);

  /* ──── Render ──── */
  const isTracing = phase !== "idle";

  return (
    <div className="relative w-screen h-screen overflow-hidden bg-black">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:px-4 focus:py-2 focus:bg-[#06B6D4] focus:text-black focus:rounded-lg"
      >
        Skip to content
      </a>

      {/* Screen reader live region for dynamic announcements */}
      <div aria-live="polite" aria-atomic="true" className="sr-only" role="status">
        {announcement}
      </div>

      <DynamicGlobe />

      {phase === "idle" && (
        <HeroInput
          startTrace={startTrace}
          traceError={traceError}
          onDismissError={() => setTraceError(null)}
        />
      )}

      {isTracing && <CompactHeader onReset={handleReset} />}

      {/* Loading overlay */}
      {phase === "loading" && (
        <div className="absolute inset-0 z-10 flex items-center justify-center pointer-events-none">
          <div className="flex flex-col items-center gap-4 pointer-events-auto">
            <div className="w-12 h-12 border-2 border-[#06B6D4]/20 border-t-[#06B6D4] rounded-full animate-spin" />
            <div className="text-center space-y-1">
              <p className="text-[14px] text-[#F4F4F5] font-medium">Discovering route</p>
              <p className="text-[13px] text-[#06B6D4] font-[family-name:var(--font-geist-mono)]">{targetUrl}</p>
            </div>
            <button
              onClick={handleReset}
              className="mt-2 px-4 py-1.5 rounded-lg text-[12px] text-[#71717A] hover:text-[#A1A1AA] border border-[rgba(255,255,255,0.08)] hover:border-[rgba(255,255,255,0.16)] transition-colors cursor-pointer"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {(phase === "animating" || phase === "complete") && (
        <>
          <Sidebar onReset={handleReset} />
          <TimeScrubber />
          <LayerToggles />
        </>
      )}
    </div>
  );
}
